(function (root) {
  'use strict';
  function makeRules(groups) {
    const variants = new Map();
    for (const group of groups) for (const id of group.variants) variants.set(String(id), group);
    const groupOf = item => variants.get(String(item.variant_id || item.id));
    const subscribed = item => Boolean(item.selling_plan && item.selling_plan !== '0' || item.selling_plan_allocation);
    function oneTime(item) {
      const extra={...item,properties:{...item.properties,_pp_one_time:'true'}};
      for (const key of ['_loop_selling_plan','_selling_plan','Trial Price','Auto-Refill']) delete extra.properties[key];
      delete extra.selling_plan;
      delete extra.selling_plan_allocation;
      return extra;
    }
    function splitAdd(items, cartItems) {
      const seen = new Set(cartItems.filter(subscribed).map(groupOf).filter(Boolean).map(g => g.slug));
      const result = [];
      for (const item of items) {
        const group = groupOf(item);
        if (!group || !subscribed(item)) { result.push({...item}); continue; }
        const quantity = Number(item.quantity == null ? 1 : item.quantity);
        if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid quantity');
        const keep = seen.has(group.slug) ? 0 : 1;
        if (keep) { result.push({...item,quantity:1}); seen.add(group.slug); }
        if (quantity > keep) {
          result.push(oneTime({...item,quantity:quantity-keep}));
        }
      }
      return result;
    }
    function excess(items) {
      const seen = new Set();
      return items.flatMap(item => {
        const group = groupOf(item);
        if (!group || !subscribed(item)) return [];
        const keep = seen.has(group.slug) ? 0 : 1;
        seen.add(group.slug);
        return item.quantity > keep ? [{item,keep,extra:item.quantity-keep}] : [];
      });
    }
    return {groupOf,subscribed,splitAdd,excess,oneTime};
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = makeRules; return; }
  const config = root.PPSubscriptionConfig;
  if (!config || root.PPSubscription) return;
  const rules = makeRules(config.groups);
  const nativeFetch = root.fetch.bind(root);
  const base = root.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  const serial = operation => {
    const run = queue.then(operation,operation);
    queue = run.catch(() => {});
    return run;
  };
  const jsonRequest = (path,payload,context={}) => nativeFetch(base + path,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(payload),signal:context.signal});
  async function readCart(context={}) {
    const response = await nativeFetch(base+'cart.js',{headers:{'Accept':'application/json'},cache:'no-store',signal:context.signal});
    if (!response.ok) throw new Error(config.messages.error);
    return response.json();
  }
  async function mutate(path,payload,context={}) {
    const response = await jsonRequest(path,payload,context);
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(data.description || (typeof data.errors==='string' ? data.errors : config.messages.error));
    return data;
  }
  function payloadOf(body) {
    if (typeof body==='string') { try { return JSON.parse(body); } catch (_) { return null; } }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const payload = {}, properties = {};
      for (const [key,value] of body.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match) properties[match[1]]=value;
        else payload[key]=value;
      }
      if (Object.keys(properties).length) payload.properties=properties;
      return payload;
    }
    return null;
  }
  function requestedItems(payload) {
    if (payload.items) return payload.items;
    const item = {id:payload.id,quantity:payload.quantity || 1};
    for (const key of ['selling_plan','properties','parent_id','parent_line_key']) if (payload[key]!=null) item[key]=payload[key];
    return [item];
  }
  function responseOf(data,original) {
    return new Response(JSON.stringify(data),{status:original.status,statusText:original.statusText,headers:{'Content-Type':'application/json'}});
  }
  function sectionOptions(payload) {
    const result = {};
    if (payload.sections) result.sections=payload.sections;
    if (payload.sections_url) result.sections_url=payload.sections_url;
    return result;
  }
  async function applyCodes(cart,sections={},refresh=false,context={}) {
    const eligible = new Set(cart.items.filter(rules.subscribed).map(rules.groupOf).filter(Boolean).map(g=>g.slug));
    const intro = config.groups.filter(g=>eligible.has(g.slug)&&!g.used).map(g=>g.code);
    const existing = (cart.discount_codes || []).map(d=>d.code).filter(c=>!c.toUpperCase().startsWith('PPFIRST-'));
    const codes = [...new Set([...existing,...intro])];
    const current = cart.discount_codes || [];
    const sameCodes = current.length === codes.length && current.every(d => codes.some(c => c.toUpperCase() === d.code.toUpperCase()));
    if (sameCodes && current.every(d => d.applicable !== false)) return refresh&&sections.sections ? mutate('cart/update.js',sections,context) : cart;
    return mutate('cart/update.js',{discount:codes.join(','),...sections},context);
  }
  async function normalize(cart,sections={},context={}) {
    let changed = false;
    for (let attempt=0;attempt<50;attempt++) {
      context.signal?.throwIfAborted();
      const adjustment=rules.excess(cart.items)[0];
      if (!adjustment) break;
      const line=adjustment.item;
      if (!adjustment.keep) {
        await mutate('cart/change.js',{id:line.key,quantity:line.quantity,selling_plan:null,properties:rules.oneTime(line).properties},context);
      } else {
        await mutate('cart/change.js',{id:line.key,quantity:adjustment.keep},context);
        try {
          await mutate('cart/add.js',{items:[rules.oneTime({id:line.variant_id,quantity:adjustment.extra,properties:line.properties||{}})]},context);
        } catch (error) {
          context.signal?.throwIfAborted();
          await mutate('cart/add.js',{items:[{id:line.variant_id,quantity:adjustment.extra,properties:line.properties||{},selling_plan:line.selling_plan_allocation.selling_plan.id}]},context);
          throw error;
        }
      }
      cart=await readCart(context);
      changed=true;
    }
    // Extra units are identified in the cart, without an interrupting toast.
    if (rules.excess(cart.items).length) throw new Error(config.messages.error);
    return applyCodes(cart,sections,changed,context);
  }
  function announce(message,error=false) {
    let notice=document.getElementById('pp-subscription-status');
    if (!notice) {
      notice=document.createElement('div');notice.id='pp-subscription-status';notice.setAttribute('role','status');notice.setAttribute('aria-live','polite');
      notice.style.cssText='position:fixed;bottom:24px;left:50%;transform:translateX(-50%);width:min(90%,560px);background:#fff7e8;color:#181818;padding:16px 20px;border:1px solid #e9b24b;border-radius:12px;z-index:10000;box-shadow:0 5px 24px #0002;font-size:14px;line-height:1.5';
      document.body.appendChild(notice);
    }
    notice.textContent=message;notice.hidden=false;
    clearTimeout(notice._hideTimer);
    if (!error) notice._hideTimer=setTimeout(()=>notice.hidden=true,8000);
  }
  async function request(url,options={},context={}) {
    context={...context,signal:context.signal || options.signal || AbortSignal.timeout(20000)};
    options={...options,signal:context.signal};
    const payload=payloadOf(options.body);
    const path=new URL(url,location.href).pathname;
    if (!payload || !/\/cart\/(add|change)(\.js)?$/.test(path)) return nativeFetch(url,options);
    return serial(async()=>{
      context.signal?.throwIfAborted();
      if (/\/add(\.js)?$/.test(path)) {
        const items=requestedItems(payload);
        if (!items.some(item=>rules.subscribed(item)&&rules.groupOf(item))) return nativeFetch(url,options);
        const cart=await readCart(context);
        const split=rules.splitAdd(items,cart.items);
        const modified=split.length!==items.length || split.some((item,i)=>Number(item.quantity||1)!==Number(items[i]?.quantity||1)||item.selling_plan!=items[i]?.selling_plan);
        const original=await jsonRequest('cart/add.js',{...sectionOptions(payload),items:split},context);
        if (!original.ok) return original;
        const added=await original.clone().json();
        try {
          // The successful add is already split. Finalize discounts and sections together.
          const eligible = new Set([...cart.items, ...split].filter(rules.subscribed).map(rules.groupOf).filter(Boolean).map(g=>g.slug));
          const intro = config.groups.filter(g=>eligible.has(g.slug)&&!g.used).map(g=>g.code);
          const existing = (cart.discount_codes||[]).map(d=>d.code).filter(c=>!c.toUpperCase().startsWith('PPFIRST-'));
          const finalized = await mutate('cart/update.js',{discount:[...new Set([...existing,...intro])].join(','),...sectionOptions(payload)},context);
          const updated=await normalize(finalized,sectionOptions(payload),context);
          if (updated.sections) added.sections=updated.sections;
          added._ppCart=updated;
          // The cart line explains the one-time price; keep adding quiet.
        } catch(error) { if(context.inlineErrors||context.signal?.aborted) throw error; announce(config.messages.addedError,true); location.assign(base+'cart'); }
        return responseOf(added,original);
      }
      const original=await nativeFetch(url,options);
      if (!original.ok) return original;
      const cart=await original.clone().json();
      if (!cart.items) return original;
      try { return responseOf(await normalize(cart,sectionOptions(payload),context),original); }
      catch(error) { if(context.inlineErrors||context.signal?.aborted) throw error; announce(error.message,true);return responseOf({...await readCart(context),errors:error.message},original); }
    });
  }
  async function checkout() {
    const context={signal:AbortSignal.timeout(20000)};
    return serial(async()=>{
      context.signal.throwIfAborted();
      const cart=await normalize(await readCart(context),{},context);
      if (rules.excess(cart.items).length) throw new Error(config.messages.error);
      location.assign(base+'checkout');
    });
  }
  // History restoration can revive pre-add cart markup after checkout.
  // A fresh navigation rebuilds drawer, prices and count together; reload is not back_forward.
  root.addEventListener?.('pageshow', event => {
    const navigation = root.performance?.getEntriesByType?.('navigation')?.[0];
    if (event.persisted || navigation?.type === 'back_forward') location.reload();
  });
  let checking=false;
  async function guard(event) {
    const form=event.target.closest('form');
    const link=event.target.closest('a[href]');
    const trigger=event.target.closest('[name="checkout"],button[type="submit"]');
    const isCheckout=(event.type==='submit'&&form&&/\/cart\/?$/.test(new URL(form.action,location.href).pathname)&&event.submitter?.name==='checkout') || (event.type==='click'&&link&&/\/checkout\/?$/.test(new URL(link.href,location.href).pathname)) || (event.type==='click'&&trigger?.name==='checkout');
    if (!isCheckout || !document.querySelector('[data-pp-subscription-cart]')) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (checking) return;
    checking=true;
    try { await checkout(); } catch(error) { announce(error.message,true); } finally { checking=false; }
  }
  document.addEventListener('submit',guard,true);
  document.addEventListener('click',guard,true);
  const reconcile = (context={}) => {
    context={...context,signal:context.signal || AbortSignal.timeout(20000)};
    return serial(async()=>{
    context.signal?.throwIfAborted();
    const cart=await normalize(await readCart(context),context.sections || {},context);
    return context.sections?.sections && !cart.sections ? mutate('cart/update.js',context.sections,context) : cart;
  });
  };
  root.PPSubscription={request,rules,reconcile};
})(typeof window==='undefined'?globalThis:window);
